import {TextInput, useFormContext, type FormFieldProps} from 'platform/components';
import {Show} from 'platform/foundation';

import {useRef, useState} from 'react';
import {get, useController, type FieldValues} from 'react-hook-form';

import {omit} from 'ramda';

import {isNotNilOrEmpty, type Nullish} from 'shared/web';

import {t} from './catalogueUi';
import {grossOfNet, optionalMoneyInput} from './moneyInput';
import {netFromGross} from './netFromGross';
import {NumberField} from './NumberField';

const THOUSANDS_SEPARATORS = /[ \u00A0\u202F]/g;
// "206000," is the pause before the decimals, not a wrong amount.
const TRAILING_DECIMAL_MARK = /[.,]$/;

const parseGross = (typed: string) =>
  optionalMoneyInput.safeParse(
    typed.replace(THOUSANDS_SEPARATORS, '').replace(TRAILING_DECIMAL_MARK, '')
  );

const netText = (net: unknown) => (typeof net === 'string' ? net : '');

export function GrossPriceField<TFieldValues extends FieldValues>(
  props: Omit<FormFieldProps<TFieldValues, 'text'>, 'type' | 'onBlur'> & {
    vatRate: string | Nullish;
    grossLabel?: string;
    onBlur?: () => void;
  }
) {
  const {trigger} = useFormContext<TFieldValues>();
  const {field, formState} = useController({control: props.control, name: props.name});
  const [typed, setTyped] = useState<string>();
  const netOnFocus = useRef('');
  const parsedTyped = typed === undefined ? undefined : parseGross(typed);

  const setNet = (net: string) => {
    field.onChange(net);
    props.control.onChange();
    props.onChange?.(net);
    void trigger(props.name);
  };
  const netOfGross = (gross: string, vatRate: string) => {
    if (gross === '') {
      return '';
    }
    // A legacy 2-decimal net already shows this gross; recomputing it would change the stored price.
    const storedNet = netText(get(formState.defaultValues, props.name));
    return grossOfNet(storedNet, vatRate) === gross ? storedNet : netFromGross(gross, vatRate);
  };
  // An invalid entry restores the net from the focus at once, not on blur: Enter submits without one.
  const changeGross = (vatRate: string, value: string | null) => {
    const text = value ?? '';
    const parsedGross = parseGross(text);
    const net = parsedGross.success ? netOfGross(parsedGross.data, vatRate) : netOnFocus.current;
    setTyped(text);
    if (net !== field.value) {
      setNet(net);
    }
  };

  return (
    <>
      <NumberField {...omit(['vatRate', 'grossLabel'], props)} />
      <Show when={isNotNilOrEmpty(props.vatRate) ? props.vatRate : null}>
        {(vatRate) => (
          <TextInput
            label={
              props.grossLabel ??
              t('entity.newVehicle.catalogue.priceGrossAtRate', 'Price gross ({rate} % VAT)', {
                rate: vatRate,
              })
            }
            value={typed ?? grossOfNet(netText(field.value), vatRate) ?? ''}
            errorMessage={parsedTyped?.error?.issues[0]?.message}
            suffix={props.suffix}
            isDisabled={field.disabled || props.isDisabled || formState.isSubmitting}
            onChange={(value) => changeGross(vatRate, value)}
            onFocus={() => {
              netOnFocus.current = netText(field.value);
            }}
            onBlur={() => {
              setTyped(undefined);
              props.onBlur?.();
            }}
          />
        )}
      </Show>
    </>
  );
}
